import {
    useState,
    useMemo,
    useCallback,
    useRef,
    useEffect,
    ChangeEventHandler,
    MouseEventHandler,
    KeyboardEventHandler,
    MouseEvent,
} from 'react';
import { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { AutocompleteProps } from '@mui/material/Autocomplete';
import { objectTypes, objectType, IAdditionalInfo } from '@api/Devices.api';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { BindedMomentChangeHandler } from '@core/interfaces/moment-change-handler';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import getBtnElementId from '@utils/getBtnElementId';
import readAsArrayBuffer from '@utils/readAsArrayBuffer';
import { getDeviceObjectKeys } from '@utils/getDeviceObjectKeys';
import { IState } from './IState';

const useAddDevice = (onClose: ICloseEvent['onClose']) => {
    const { devices, groups, download, lang } = useStore();
    const key = useKeyDown();

    const types = useMemo(() => Object.values(objectTypes), []);
    const values = {
        types,
    };

    const initState: IState = {
        deviceName: '',
        deviceNameError: false,
        imei: '',
        type: null,
        keys: [],
        additionalInfo: {},
        imeiError: false,
        password: '',
        passwordError: false,
        deviceIconNum: 1,
        iconSelectVisible: false,
        iconSelectEl: null,
        groupName: '',
    };
    const [state, setState] = useState(initState);

    const iconSelectEl = useRef<HTMLButtonElement | null>(null);
    const refs = {
        iconSelectEl,
    };

    const deviceNameValidate = useCallback(
        (name: string) => {
            if (!name) {
                setState((prev) => ({
                    ...prev,
                    deviceNameError: true,
                }));
                return false;
            } else {
                setState((prev) => ({
                    ...prev,
                    deviceNameError: false,
                }));
                return true;
            }
        },
        [setState],
    );

    const imeiValidate = useCallback(
        (imei: string) => {
            const regExp = /^\d{15,16}$/;
            if (!imei || !regExp.test(imei)) {
                setState((prev) => ({
                    ...prev,
                    imeiError: true,
                }));
                return false;
            } else {
                setState((prev) => ({
                    ...prev,
                    imeiError: false,
                }));
                return true;
            }
        },
        [setState],
    );

    const handleInputClick = useCallback(() => {
        devices.resetServerError();
        devices.resetDeviceAlreadyExistError();
        devices.resetDeviceAlreadyInUseError();
        devices.resetImeiNotExistError();
        devices.resetNoRightsError();
        download.resetUploadError();
        devices.resetWrongPasswordError();
        setState((prev) => ({
            ...prev,
            deviceNameError: false,
            imeiError: false,
            passwordError: false,
        }));
    }, [setState]);

    const handleDeviceNameChange: ChangeEventHandler<HTMLInputElement> =
        useCallback(
            (e) => {
                handleInputClick();
                const deviceName = e.target.value;
                setState((prev) => ({
                    ...prev,
                    deviceName,
                }));
            },
            [setState, handleInputClick],
        );

    const handleImeiChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        (e) => {
            handleInputClick();
            const filter = /\D/g;
            const reqExp = /.{0,16}/;
            const val = e.target.value.replace(filter, '').match(reqExp);
            const imei = val ? val[0] : '';
            setState((prev) => ({
                ...prev,
                imei,
            }));
        },
        [setState, handleInputClick],
    );

    const handlePasswordChange: ChangeEventHandler<HTMLInputElement> =
        useCallback(
            (e) => {
                handleInputClick();
                const password = e.target.value;
                setState((prev) => ({
                    ...prev,
                    password,
                }));
            },
            [setState, handleInputClick],
        );

    const handleGroupNameChange: AutocompleteProps<
        string,
        boolean,
        boolean,
        boolean
    >['onInputChange'] = useCallback(
        (e: React.SyntheticEvent<Element, Event>, groupName: string) => {
            setState((prev) => ({
                ...prev,
                groupName,
            }));
        },
        [setState],
    );

    const handleTypeChange: SelectProps<objectType>['onChange'] = useCallback(
        (e: SelectChangeEvent<objectType>) => {
            handleInputClick();
            const type = e.target.value as objectType;
            setState((prev) => ({
                ...prev,
                type,
                keys: getDeviceObjectKeys(type),
            }));
        },
        [handleInputClick, setState],
    );

    const handleAddClick = useCallback(() => {
        handleInputClick();
        const {
            deviceName,
            imei,
            password,
            groupName,
            deviceIconNum,
            type,
            additionalInfo: info,
        } = state;        
        const isDeviceNameValid = deviceNameValidate(deviceName);
        const isImeiValid = imeiValidate(imei);
        let additionalInfo: IAdditionalInfo = {};
        if (type) {
            const items = getDeviceObjectKeys(type);
            additionalInfo = items.reduce((acc, item) => {
                const key = item.name;
                const value = info[key as keyof typeof info] ?? '';
                return { ...acc, [key]: value };
            }, {} as IAdditionalInfo);
        }
        const name = deviceName;
        const icon = deviceIconNum;
        const [group] = groups.groupsList.filter(
            (group) => group.name === groupName,
        );
        if (!isDeviceNameValid || !isImeiValid) return;
        if (!group && groupName !== '') {
            const args = {
                imei,
                name,
                password,
                groupName,
                icon,
                type,
                additionalInfo,
            };
            devices.addDeviceAndGroup(args);
        } else {            
            const groupId = group?.id;
            const args = {
                imei,
                name,
                password,
                groupId,
                icon,
                type,
                additionalInfo,
            };
            devices.addDevice(args);
        }
    }, [state, deviceNameValidate, imeiValidate, groups.groupsList]);

    const handleTemplateDownload = useCallback(() => {
        handleInputClick();
        download.downloadDevicesTemplate();
    }, [handleInputClick]);

    const handleAddSuccess = useCallback(() => {
        handleInputClick();
        onClose();
        devices.resetOperationSuccess();
    }, [handleInputClick]);

    const handleSelectIconBtnClick: MouseEventHandler<HTMLButtonElement> =
        useCallback(
            (e: MouseEvent<HTMLButtonElement>) => {
                handleInputClick();
                e.stopPropagation();
                setState((prev) => ({
                    ...prev,
                    iconSelectEl: iconSelectEl.current,
                    iconSelectVisible: !prev.iconSelectVisible,
                }));
            },
            [handleInputClick, setState],
        );

    const handleSelectIconWindowClose = useCallback(() => {
        handleInputClick();
        setState((prev) => ({ ...prev, iconSelectVisible: false }));
    }, [handleInputClick, setState]);

    const handleIconClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (e) => {
            handleInputClick();
            const id = getBtnElementId(e.target as HTMLButtonElement);
            const deviceIconNum = id ? parseInt(id, 10) : 0;
            setState((prev) => ({
                ...prev,
                deviceIconNum,
                iconSelectVisible: false,
            }));
        },
        [handleInputClick, setState],
    );

    const handleDevicesUpload: ChangeEventHandler<HTMLInputElement> =
        useCallback(async ({ target }) => {
            const files = target.files;
            if (files) {
                const [file] = files;
                const buffer = await readAsArrayBuffer(file);
                download.uploadDevices(buffer);
            }
        }, []);

    const handleKey = useCallback(
        (key: string) => {
            switch (key) {
                case 'Escape':
                    onClose();
                    break;
                case 'Enter':
                    handleAddClick();
                    break;
            }
        },
        [handleAddClick],
    );

    const handleKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
        (e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                handleKey(key);
            }
        },
        [handleKey],
    );

    const handleBtnKeyDown: KeyboardEventHandler<HTMLButtonElement> =
        useCallback((e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                const target = e.target as HTMLButtonElement;
                target.click();
            }
        }, []);

    const handleSelectKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
        (e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
            }
        },
        [],
    );

    const handleAdditionalInfoChange: ChangeEventHandler<HTMLInputElement> =
        useCallback(
            (e) => {
                handleInputClick();
                const target = e.target;
                const key = target.id;
                const value = target.value;
                setState((prev) => ({
                    ...prev,
                    additionalInfo: {
                        ...prev.additionalInfo,
                        [key]: value,
                    },
                }));
            },
            [handleInputClick, setState],
        );

    const handleAdditionalInfoDateChange: BindedMomentChangeHandler =
        useCallback(
            (id, e) => {
                handleInputClick();
                const value = e ? e.format('DD.MM.YYYY') : '';
                setState((prev) => ({
                    ...prev,
                    additionalInfo: {
                        ...prev.additionalInfo,
                        [id]: value,
                    },
                }));
            },
            [handleInputClick, setState],
        );

    const handlers = {
        handleDeviceNameChange,
        handleImeiChange,
        handlePasswordChange,
        handleGroupNameChange,
        handleTypeChange,
        handleAdditionalInfoChange,
        handleAdditionalInfoDateChange,
        handleAddClick,
        handleInputClick,
        handleSelectIconBtnClick,
        handleSelectIconWindowClose,
        handleIconClick,
        handleKeyDown,
        handleTemplateDownload,
        handleDevicesUpload,
        handleSelectKeyDown,
        handleBtnKeyDown,
    };

    useEffect(() => {
        handleInputClick();
        const element = document.activeElement as HTMLElement;
        if (element) {
            element.blur();
        }
        return () => {
            handleInputClick();
        };
    }, []);
    useEffect(() => {
        if (devices.operationSuccess) {
            handleAddSuccess();
        }
    }, [devices.operationSuccess]);
    useEffect(() => {
        handleKey(key);
    }, [key]);
    useEffect(() => {
        handleInputClick();
    }, [state.groupName]);

    return {
        devices,
        groups,
        download,
        lang,
        state,
        handlers,
        refs,
        values,
    };
};

export default useAddDevice;
